import { type CardWordChange, type ConflictZone, conflictZonesOfParagraph } from "@common/utils/redline/text/card-collisions";
import type { DiffUnitWithTrailingSeparator } from "@common/utils/redline/text/word-tokenizer";

import { type PageRevisionRun } from "./page-revisions";
import { type ParagraphGroupMember, type TrackerParagraphGroup, allMembersOf } from "./paragraph-groups";

export type WordLikeSegmentKind = "deleted" | "inserted";

export interface WordLikeSegment {
  kind: WordLikeSegmentKind;
  text: string;
}

export interface WordLikeWordToken {
  kind: "word";
  text: string;
  trailingText: string;
  wordIndex: number;
  commentIds: string[];
  pageInsertedBy: string | null;
}

export interface WordLikePageDeletionToken {
  kind: "pageDeleted";
  text: string;
  trailingText: string;
  author: string;
  commentIds: string[];
}

export interface WordLikeChangeToken {
  kind: "change";
  actionId: string;
  cardNumber: number;
  trailingText: string;
  commentIds: string[];
  segments: WordLikeSegment[];
}

export interface WordLikeAlternativeCard {
  actionId: string;
  cardNumber: number;
}

export interface WordLikeAlternative {
  insertedText: string;
  cards: WordLikeAlternativeCard[];
}

export interface WordLikeConflictToken {
  kind: "conflict";
  deletedText: string;
  trailingText: string;
  alternatives: WordLikeAlternative[];
  contested: boolean;
  commentIds: string[];
}

export type WordLikeToken = WordLikeWordToken | WordLikePageDeletionToken | WordLikeChangeToken | WordLikeConflictToken;

export interface CommentedMember {
  member: ParagraphGroupMember;
  color: string;
  activeColor: string;
}

export const COMMENT_HIGHLIGHT_COLORS = ["#FDE68A", "#BFDBFE", "#FBCFE8", "#BBF7D0", "#DDD6FE", "#FED7AA"];
export const COMMENT_HIGHLIGHT_ACTIVE_COLORS = ["#FBBF24", "#60A5FA", "#F472B6", "#4ADE80", "#A78BFA", "#FB923C"];
export const CHANGE_COLORS = ["#2563EB", "#7C3AED", "#D97706", "#059669", "#DB2777", "#0891B2", "#4338CA", "#B45309"];

export const isCommentMember = (member: ParagraphGroupMember): boolean => member.placement === "commentOnPortion" || member.placement === "commentOnParagraph";

function byPositionThenCardNumber(left: ParagraphGroupMember, right: ParagraphGroupMember): number {
  const positionOrder = left.position - right.position;
  return Number.isNaN(positionOrder) || positionOrder === 0 ? left.action.cardNumber - right.action.cardNumber : positionOrder;
}

export function commentMembersOf(group: TrackerParagraphGroup): CommentedMember[] {
  return allMembersOf(group)
    .filter(isCommentMember)
    .sort(byPositionThenCardNumber)
    .map((member, index) => ({
      member,
      color: COMMENT_HIGHLIGHT_COLORS[index % COMMENT_HIGHLIGHT_COLORS.length],
      activeColor: COMMENT_HIGHLIGHT_ACTIVE_COLORS[index % COMMENT_HIGHLIGHT_ACTIVE_COLORS.length],
    }));
}

export function editMembersOf(group: TrackerParagraphGroup): ParagraphGroupMember[] {
  return allMembersOf(group).filter((member) => member.placement === "edit").sort((left, right) => left.action.cardNumber - right.action.cardNumber);
}

export function newParagraphMembersOf(group: TrackerParagraphGroup): ParagraphGroupMember[] {
  return allMembersOf(group).filter((member) => member.placement === "newParagraph").sort((left, right) => left.action.cardNumber - right.action.cardNumber);
}

export function changeColorsOf(group: TrackerParagraphGroup): Map<string, string> {
  const changeMembers = [...editMembersOf(group), ...newParagraphMembersOf(group)].sort((left, right) => left.action.cardNumber - right.action.cardNumber);
  return new Map(changeMembers.map((member, index) => [member.action.id, CHANGE_COLORS[index % CHANGE_COLORS.length]]));
}

interface OpenChangeToken {
  token: WordLikeChangeToken;
  endWordIndex: number;
}

function alternativesOf(zone: ConflictZone, cardNumberOf: Map<string, number>): WordLikeAlternative[] {
  const alternatives: WordLikeAlternative[] = [];
  const optionsByCardNumber = [...zone.options].sort((left, right) => (cardNumberOf.get(left.cardId) ?? 0) - (cardNumberOf.get(right.cardId) ?? 0));
  for (const option of optionsByCardNumber) {
    const card: WordLikeAlternativeCard = { actionId: option.cardId, cardNumber: cardNumberOf.get(option.cardId) ?? 0 };
    const sameText = alternatives.find((alternative) => alternative.insertedText === option.optionText);
    if (sameText !== undefined) sameText.cards.push(card);
    else alternatives.push({ insertedText: option.optionText, cards: [card] });
  }
  return alternatives;
}

function pageInsertionAuthorAt(offset: number, revisions: PageRevisionRun[]): string | null {
  return revisions.find((run) => run.kind === "inserted" && run.acceptedStart <= offset && offset < run.acceptedEnd)?.author ?? null;
}

function pageDeletionsByWordIndex(revisions: PageRevisionRun[], baseUnits: DiffUnitWithTrailingSeparator[]): Map<number, PageRevisionRun[]> {
  const deletionsByWordIndex = new Map<number, PageRevisionRun[]>();
  for (const run of revisions) {
    if (run.kind !== "deleted" || run.text.trim().length === 0) continue;
    const firstWordAtOrAfter = baseUnits.findIndex((unit) => unit.startOffset >= run.acceptedStart);
    const wordIndex = firstWordAtOrAfter < 0 ? baseUnits.length : firstWordAtOrAfter;
    deletionsByWordIndex.set(wordIndex, [...(deletionsByWordIndex.get(wordIndex) ?? []), run]);
  }
  return deletionsByWordIndex;
}

function textAfterUnitsOf(baseUnits: DiffUnitWithTrailingSeparator[], endWordIndex: number, startWordIndex: number): string {
  return endWordIndex > startWordIndex ? baseUnits[endWordIndex - 1]?.trailingSeparator ?? "" : "";
}

function changeSegmentsOf(baseText: string, change: CardWordChange): WordLikeSegment[] {
  const deletedText = baseText.slice(change.startOffset, change.endOffset);
  return [
    ...(deletedText.length > 0 ? [{ kind: "deleted" as const, text: deletedText }] : []),
    ...(change.replacementText.length > 0 ? [{ kind: "inserted" as const, text: change.replacementText }] : []),
  ];
}

export function wordLikeTokensOf(group: TrackerParagraphGroup): WordLikeToken[] {
  const baseUnits = group.baseUnits;
  const pageDeletions = pageDeletionsByWordIndex(group.pageRevisions, baseUnits);
  const anchoredComments = allMembersOf(group).filter((member) => isCommentMember(member) && member.wordSpan !== null);
  const commentIdsAt = (wordIndex: number): string[] => anchoredComments
    .filter((comment) => comment.wordSpan !== null && comment.wordSpan.startWordIndex <= wordIndex && wordIndex < comment.wordSpan.endWordIndex)
    .map((comment) => comment.action.id);
  const commentIdsOver = (startWordIndex: number, endWordIndex: number): string[] => {
    const ids = new Set<string>();
    for (let wordIndex = startWordIndex; wordIndex < Math.max(endWordIndex, startWordIndex + 1); wordIndex += 1) commentIdsAt(wordIndex).forEach((id) => ids.add(id));
    return Array.from(ids);
  };
  const pendingEdits = editMembersOf(group).filter((member) => !member.action.isApplied && member.changes.length > 0);
  const cardNumberOf = new Map(pendingEdits.map((edit) => [edit.action.id, edit.action.cardNumber]));
  const zoning = conflictZonesOfParagraph(group.baseText, pendingEdits.map((edit) => ({ cardId: edit.action.id, changes: edit.changes })));
  const coveredWordIndexes = new Set<number>();
  for (const uncontested of zoning.uncontestedChanges) {
    for (let wordIndex = uncontested.change.startWordIndex; wordIndex < uncontested.change.endWordIndex; wordIndex += 1) coveredWordIndexes.add(wordIndex);
  }
  for (const zone of zoning.zones) {
    for (let wordIndex = zone.zoneStartWordIndex; wordIndex < zone.zoneEndWordIndex; wordIndex += 1) coveredWordIndexes.add(wordIndex);
  }
  const tokens: WordLikeToken[] = [];
  let openChange: OpenChangeToken | null = null;
  const changeTokenFor = (cardId: string, change: CardWordChange): WordLikeChangeToken => {
    const trailingText = textAfterUnitsOf(baseUnits, change.endWordIndex, change.startWordIndex);
    const continuesOpenChange = openChange !== null && openChange.token.actionId === cardId && openChange.endWordIndex === change.startWordIndex;
    if (continuesOpenChange && openChange !== null) {
      openChange.endWordIndex = change.endWordIndex;
      if (change.endWordIndex > change.startWordIndex) openChange.token.trailingText = trailingText;
      commentIdsOver(change.startWordIndex, change.endWordIndex).forEach((id) => { if (!openChange?.token.commentIds.includes(id)) openChange?.token.commentIds.push(id); });
      return openChange.token;
    }
    const token: WordLikeChangeToken = { kind: "change", actionId: cardId, cardNumber: cardNumberOf.get(cardId) ?? 0, trailingText, commentIds: commentIdsOver(change.startWordIndex, change.endWordIndex), segments: [] };
    tokens.push(token);
    openChange = { token, endWordIndex: change.endWordIndex };
    return token;
  };
  for (let wordIndex = 0; wordIndex <= baseUnits.length; wordIndex += 1) {
    for (const deletion of pageDeletions.get(wordIndex) ?? []) {
      tokens.push({ kind: "pageDeleted", text: deletion.text.trim(), trailingText: "", author: deletion.author, commentIds: [] });
      openChange = null;
    }
    for (const zone of zoning.zones.filter((candidate) => candidate.zoneStartWordIndex === wordIndex)) {
      const alternatives = alternativesOf(zone, cardNumberOf);
      tokens.push({ kind: "conflict", deletedText: zone.originalText, trailingText: textAfterUnitsOf(baseUnits, zone.zoneEndWordIndex, zone.zoneStartWordIndex), alternatives, contested: alternatives.length > 1, commentIds: commentIdsOver(zone.zoneStartWordIndex, zone.zoneEndWordIndex) });
      openChange = null;
    }
    const uncontestedHere = zoning.uncontestedChanges
      .filter((candidate) => candidate.change.startWordIndex === wordIndex)
      .sort((left, right) => (cardNumberOf.get(left.cardId) ?? 0) - (cardNumberOf.get(right.cardId) ?? 0));
    for (const uncontested of uncontestedHere) {
      changeTokenFor(uncontested.cardId, uncontested.change).segments.push(...changeSegmentsOf(group.baseText, uncontested.change));
    }
    if (wordIndex < baseUnits.length && !coveredWordIndexes.has(wordIndex)) {
      const unit = baseUnits[wordIndex];
      tokens.push({ kind: "word", text: unit.text, trailingText: unit.trailingSeparator, wordIndex, commentIds: commentIdsAt(wordIndex), pageInsertedBy: pageInsertionAuthorAt(unit.startOffset, group.pageRevisions) });
      openChange = null;
    }
  }
  return tokens;
}

